import React from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import './Modal.css';

/**
 * Modal Component - Reusable modal dialog
 * Supports confirm, input, and custom content modals
 */
const Modal = ({
  isOpen,
  onClose = null,
  title = '',
  message = '',
  type = 'confirm', // 'confirm', 'input', 'custom'
  confirmText = 'OK',
  cancelText = 'Cancel',
  onConfirm = null,
  onCancel = null,
  showCancel = true,
  icon = null,
  children = null,
  inputValue = '',
  onInputChange = null,
  inputPlaceholder = '',
  inputLabel = null,
  isDangerous = false,
  isLoading = false,
  className = '',
  focusCancel = false,
  // A custom body with the usual footer (Cancel, the confirm button), and the confirm held until the body allows it.
  withFooter = false,
  confirmDisabled = false
}) => {
  // A dialog named by its title (and described by its message), for screen readers and getByRole('dialog', { name }).
  const titleId = React.useId();
  const messageId = React.useId();
  // `focusCancel` (a question before something destructive): Cancel has the focus when it opens, so Enter never
  // confirms by accident, and Escape cancels from anywhere in the page (not while `isLoading`).
  const cancelRef = React.useRef(null);
  const cancelNow = React.useRef(null);
  cancelNow.current = onCancel || onClose;
  React.useEffect(() => {
    if (!isOpen || !focusCancel) { return undefined; }
    cancelRef.current?.focus();
    const onKey = (event) => {
      if (event.key === 'Escape' && !isLoading && cancelNow.current) {
        event.preventDefault();
        cancelNow.current();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen, focusCancel, isLoading]);
  if (!isOpen) return null;

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget && onClose) {
      onClose();
    }
  };

  const handleConfirm = () => {
    if (onConfirm) {
      onConfirm(inputValue);
    } else if (onClose && !showCancel) {
      // If no onConfirm and no cancel button, treat confirm as close
      onClose();
    }
  };

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    } else if (onClose) {
      onClose();
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && type === 'input' && inputValue) {
      handleConfirm();
    } else if (e.key === 'Escape') {
      handleCancel();
    }
  };

  return ReactDOM.createPortal(
    <div className="modal-overlay" onClick={handleOverlayClick}>
      <div
        aria-describedby={message && type !== 'custom' ? messageId : undefined}
        aria-labelledby={title ? titleId : undefined}
        aria-modal="true"
        className={`modal-content ${className}`}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
      >
        {/* Header */}
        <div className="modal-header">
          {icon && <span className="modal-icon">{icon}</span>}
          <h3 className="modal-title" id={titleId}>{title}</h3>
          {onClose && (
            <button
              className="modal-close-btn"
              onClick={handleCancel}
              type="button"
            >
              {'×'}
            </button>
          )}
        </div>

        {/* Body */}
        <div className="modal-body">
          {type === 'input' ? (
            <>
              {inputLabel && <label className="modal-input-label">{inputLabel}</label>}
              <input
                autoFocus
                className="modal-input"
                onChange={(e) => onInputChange && onInputChange(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={inputPlaceholder}
                type="text"
                value={inputValue || ''}
              />
              {message && <p className="modal-message" id={messageId}>{message}</p>}
            </>
          ) : type === 'custom' ? (
            children
          ) : (
            <p className="modal-message" id={messageId}>{message}</p>
          )}
        </div>

        {/* Footer */}
        {(type !== 'custom' || withFooter) && (
          <div className="modal-footer">
            {showCancel && (
              <button
                className="modal-btn modal-btn-cancel"
                disabled={isLoading}
                onClick={handleCancel}
                ref={cancelRef}
                type="button"
              >
                {cancelText}
              </button>
            )}
            <button
              className={`modal-btn modal-btn-confirm ${isDangerous ? 'dangerous' : ''}`}
              disabled={isLoading || confirmDisabled || (type === 'input' && !inputValue)}
              onClick={handleConfirm}
              type="button"
            >
              {isLoading ? '⏳' : confirmText}
            </button>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
};

Modal.propTypes = {
  cancelText: PropTypes.string,
  children: PropTypes.node,
  className: PropTypes.string,
  confirmDisabled: PropTypes.bool,
  confirmText: PropTypes.string,
  focusCancel: PropTypes.bool,
  icon: PropTypes.string,
  inputLabel: PropTypes.string,
  inputPlaceholder: PropTypes.string,
  inputValue: PropTypes.string,
  isDangerous: PropTypes.bool,
  isLoading: PropTypes.bool,
  isOpen: PropTypes.bool.isRequired,
  message: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  onCancel: PropTypes.func,
  onClose: PropTypes.func,
  onConfirm: PropTypes.func,
  onInputChange: PropTypes.func,
  showCancel: PropTypes.bool,
  title: PropTypes.string,
  withFooter: PropTypes.bool,
  type: PropTypes.oneOf(['confirm', 'input', 'custom'])
};

export default Modal;

/** A Modal, or (`embedded`) just its content: for a panel that is also a tab of another dialog. */
export function ModalFrame({ embedded = false, children = null, ...props }) {
  return embedded ? <>{children}</> : <Modal {...props}>{children}</Modal>;
}

ModalFrame.propTypes = {
  children: PropTypes.node,
  embedded: PropTypes.bool,
};
